import { useEffect, useRef, useState } from 'react';
import { subscribe } from '@/shared/lib/mqtt/client';
import { useSession } from '@/features/auth/model/use-session';
import type { MemberPositionDto } from '@/shared/api/generated/model';

/** Where one volunteer was, the last time they said so. */
export interface LiveTrack {
  userId: string;
  fullName: string;
  longitude: number;
  latitude: number;
  recordedAt: string;
}

/**
 * The record and the feed as one picture: everybody's newest stored position, moved by whatever the
 * broker has delivered since.
 *
 * ⚠️ **The feed alone left this map empty** (11 October 2026). A subscription delivers what is
 * published AFTER it opens, so a map opened on a running search drew nobody until each volunteer
 * next moved — and a volunteer standing beside a casualty does not move. `GET …/positions` is what
 * is drawn first.
 *
 * ⚠️ **By the time a fix was TAKEN, never by which source spoke last.** The record lags the feed —
 * a batch reaches the database up to a minute after the broker delivered the same point — so a
 * re-read that simply won would pull every moving marker back along its own track.
 */
export function mergeTracks(
  last: readonly MemberPositionDto[] | undefined,
  live: Record<string, LiveTrack>,
): Record<string, LiveTrack> {
  const merged: Record<string, LiveTrack> = {};

  for (const member of last ?? []) {
    if (
      typeof member.longitude !== 'number' ||
      typeof member.latitude !== 'number' ||
      !member.recordedAt
    ) {
      // Joined and not reported yet: nothing to draw, and (0, 0) is in the Atlantic.
      continue;
    }

    merged[member.userId] = {
      userId: member.userId,
      fullName: member.fullName,
      longitude: member.longitude,
      latitude: member.latitude,
      recordedAt: member.recordedAt,
    };
  }

  for (const track of Object.values(live)) {
    const held = merged[track.userId];

    if (!held || Date.parse(track.recordedAt) >= Date.parse(held.recordedAt)) {
      merged[track.userId] = { ...track, fullName: track.fullName || held?.fullName || '' };
    }
  }

  return merged;
}

/** The members worth watching: who they are, and what to call them on the map. */
export interface WatchedMember {
  userId: string;
  fullName: string;
}

/**
 * The live position of every member on a mission (§5.9).
 *
 * ⚠️ **From MQTT, never by polling the API.** §5.9 says so outright, and the reason is arithmetic:
 * a control room watching thirty volunteers at a five-second poll is 21,600 requests an hour for
 * data the broker is already pushing. The API stays the source of the record — the rule engine
 * writes every point to PostgreSQL (§8.7) — and this is the same data arriving as it happens.
 *
 * ⚠️ **The grant this once waited on now exists** — `AuthorizeTopicQueryHandler.MayWatchAnyMission`,
 * added the same afternoon this file was written and never noted here. `head-of-control`,
 * `desk-control` and `system-administrator` may SUBSCRIBE to `chat`, `events`, `track` and
 * `presence` on any mission, member or not, and `AuthorizeTopicTests` pins all three by name.
 *
 * §8.3 rule 3 — "a user must not be able to subscribe to a mission they do not belong to" — is not
 * contradicted by this: the question it left open was what makes an operator belong, and the answer
 * taken was the standing control role rather than a per-mission `ControlShift`. The narrower reading
 * is still available if the association asks for it; widening it further is §18.6's decision, not
 * ours.
 *
 * ⚠️ **Watching is not writing.** The same handler refuses these roles every publish, so a marker
 * can never be moved from the control room to where the volunteer is not.
 *
 * ⚠️ **One subscription per member, because a wildcard is refused.** §8.3's ACL denies
 * `mission/+/track/+` and `mission/{id}/track/+` alike, which was measured against the live broker
 * — a subscription to every search the charity is running is exactly what the scheme exists to
 * prevent. So the caller passes the members and this subscribes to each by name.
 */
export function useMissionTracks(
  missionId: string,
  members: readonly WatchedMember[],
): Record<string, LiveTrack> {
  const { session } = useSession();
  const token = session?.accessToken;

  const [tracks, setTracks] = useState<Record<string, LiveTrack>>({});

  // ⚠️ Arrivals are collected here and flushed on a frame, never straight into state.
  //
  // §5.9: "batch marker updates on requestAnimationFrame". Thirty volunteers reporting every two
  // seconds is fifteen renders a second of a map with thirty markers on it, and React would run
  // every one of them — the screen cannot show more than one frame anyway.
  const pending = useRef<Record<string, LiveTrack>>({});
  const frame = useRef<number | null>(null);

  // Names are looked up when a message arrives, so a member joining does not have to re-subscribe
  // everybody else.
  const names = useRef<Record<string, string>>({});
  names.current = Object.fromEntries(members.map((member) => [member.userId, member.fullName]));

  // A stable key: re-subscribing on every render of the parent would be a subscribe/unsubscribe
  // storm against the broker, which is how a control room's live feed becomes the thing that takes
  // the broker down.
  const watched = members.map((member) => member.userId).sort().join(',');

  useEffect(() => {
    const flush = () => {
      frame.current = null;

      const arrived = pending.current;
      pending.current = {};

      if (Object.keys(arrived).length > 0) {
        setTracks((current) => ({ ...current, ...arrived }));
      }
    };

    const stops = watched
      .split(',')
      .filter(Boolean)
      .map((userId) =>
        subscribe(`mission/${missionId}/track/${userId}`, (payload) => {
          const point = payload as Partial<LiveTrack> | null;

          // ⚠️ A malformed point is dropped, never drawn at (0, 0) — which is in the Atlantic, and
          // on a map of Al-Jouf would send the whole view there.
          if (
            !point ||
            typeof point.longitude !== 'number' ||
            typeof point.latitude !== 'number' ||
            !Number.isFinite(point.longitude) ||
            !Number.isFinite(point.latitude)
          ) {
            return;
          }

          pending.current[userId] = {
            userId,
            fullName: names.current[userId] ?? '',
            longitude: point.longitude,
            latitude: point.latitude,
            recordedAt: typeof point.recordedAt === 'string' ? point.recordedAt : '',
          };

          frame.current ??= requestAnimationFrame(flush);
        }),
      );

    return () => {
      for (const stop of stops) {
        stop();
      }

      if (frame.current !== null) {
        cancelAnimationFrame(frame.current);
        frame.current = null;
      }

      // ⚠️ Dropped on unmount rather than kept. A position from the last time this screen was open
      // is a volunteer's *former* location drawn as their current one — the one kind of staleness a
      // control room must never be shown.
      pending.current = {};
      setTracks({});
    };

    // The token is a dependency because the shared client is replaced when it is renewed (§12.1),
    // and a subscription on the old connection delivers nothing.
  }, [missionId, watched, token]);

  return tracks;
}
